import { FormEvent, useEffect, useState } from 'react';
import { clientApi, clientErrorMessage } from '../lib/api';
import { getStoredClientName, setStoredClientName } from '../lib/clientName';
import { downloadApprovalCertificate } from '../lib/downloadPdf';
import type { ScriptStatus } from '../../shared/types';
import Eyebrow from './Eyebrow';
import Button from './Button';
import Confetti from './Confetti';

const DASHBOARD_BASE = 'https://dashboard.sheahanstudios.com';

function roundLabel(status: ScriptStatus, noun: string): string {
  if (status === 'v2') return 'Round 2';
  if (status === 'v3') return 'Round 3';
  return `this ${noun}`;
}

interface Props {
  slug: string;
  /** 'brief' on an interview brief, so the ceremony's copy says "brief". */
  kind?: 'script' | 'brief';
  title: string;
  clientName: string;
  status: ScriptStatus;
  dashboardSlug: string | null;
  /** Fired once the approve call succeeds — the parent refetches so the page
   *  behind the modal flips to its approved state. */
  onApproved: () => void;
  onClose: () => void;
}

/**
 * The approval ceremony — replaces the old two-click approve with a deliberate
 * moment: type your name to sign, a beat of celebration, then a confirmation
 * that points to the dashboard for what's next and offers the certificate.
 * Production logistics (shoot, edit, delivery) live on the dashboard, so this
 * intentionally has no calendar invite or shoot details.
 */
export default function ApprovalCeremony({
  slug,
  kind = 'script',
  title,
  clientName,
  status,
  dashboardSlug,
  onApproved,
  onClose,
}: Props) {
  const [phase, setPhase] = useState<'form' | 'done'>('form');
  const [name, setName] = useState(() => getStoredClientName());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approvedAtMs, setApprovedAtMs] = useState<number>(() => Date.now());
  const [certBusy, setCertBusy] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Escape only backs out of the form — once approved, there's no undo.
      if (e.key === 'Escape' && phase === 'form' && !busy) onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [phase, busy, onClose]);

  const trimmed = name.trim();
  const nameValid = trimmed.length >= 2;
  const firstName = trimmed.split(/\s+/)[0] || 'there';
  const noun = kind === 'brief' ? 'brief' : 'script';

  async function handleApprove(e: FormEvent) {
    e.preventDefault();
    if (!nameValid) {
      setError('Please type your full name so we have it on record.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await clientApi.approveScript(slug, trimmed);
      setStoredClientName(trimmed);
      setApprovedAtMs(res.approved_at ?? Date.now());
      setPhase('done');
      onApproved();
    } catch (err) {
      setError(
        clientErrorMessage(err, 'That didn’t go through. Please try again in a moment.'),
      );
      setBusy(false);
    }
  }

  async function handleCertificate() {
    setCertBusy(true);
    try {
      await downloadApprovalCertificate({
        title,
        clientName,
        approvedByName: trimmed,
        approvedAtMs,
        status,
        slug,
      });
    } catch {
      /* A failed certificate download shouldn't disrupt the confirmation. */
    } finally {
      setCertBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-ink-900/70 backdrop-blur-md flex items-center justify-center px-6 z-50 animate-fade-in"
      onClick={phase === 'form' && !busy ? onClose : undefined}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="relative overflow-hidden w-full max-w-md bg-card border border-line rounded-md p-8 shadow-flare-lg/30 animate-scale-in"
      >
        {phase === 'form' ? (
          <form onSubmit={handleApprove}>
            <Eyebrow accent>// Approve this {noun}</Eyebrow>
            <h2 className="font-display font-bold text-[24px] leading-[1.15] tracking-[-0.01em] mt-3 mb-2">
              You’re signing off {roundLabel(status, noun)}
            </h2>
            <p className="text-muted text-[14px] leading-[1.55] mb-6">
              Approving locks the {noun} for filming. You can still email Henry with anything
              after. Nothing’s set in stone except the good kind.
            </p>
            <label className="block">
              <span className="block font-mono text-[11px] uppercase tracking-[0.08em] text-muted mb-2">
                Type your name to sign
              </span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                aria-invalid={error ? true : undefined}
                className="
                  w-full px-4 py-3 bg-elevated border border-line rounded-sm
                  text-fg text-[15px]
                  focus:border-accent focus:shadow-focus focus:outline-none
                  transition-all duration-150
                "
              />
            </label>
            {error && (
              <p role="alert" className="text-[13px] text-accent mt-2">
                {error}
              </p>
            )}
            <div className="flex flex-col md:flex-row md:items-center gap-3 mt-6">
              <Button type="submit" variant="primary" glow className="w-full md:w-auto" disabled={busy || !nameValid}>
                {busy ? 'Approving…' : 'Approve. Let’s film it'}
              </Button>
              <Button type="button" variant="ghost" className="w-full md:w-auto" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="relative text-center">
            <Confetti />
            <div className="relative">
              <div className="text-[2rem] leading-none" aria-hidden>
                🎉
              </div>
              <Eyebrow accent className="block mt-3">
                // Approved · {formatDateTime(approvedAtMs)}
              </Eyebrow>
              <h2 className="font-display font-bold text-[24px] leading-[1.15] tracking-[-0.01em] mt-2 mb-2">
                That’s a wrap on the {noun}, {firstName}.
              </h2>
              <p className="text-muted text-[14px] leading-[1.55] mb-5 max-w-[42ch] mx-auto">
                <strong className="text-fg font-semibold">{title}</strong> is approved and locked
                for filming. I’ll take it from here.
              </p>
              {dashboardSlug ? (
                <a
                  href={`${DASHBOARD_BASE}/${dashboardSlug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="
                    flex items-center justify-center gap-2 w-full mb-4
                    rounded-sm border border-accent/30 bg-accent/5 text-accent
                    font-semibold text-[14px] px-4 py-3
                    hover:bg-accent/10 transition-colors
                  "
                >
                  Track what happens next on your dashboard →
                </a>
              ) : (
                <p className="text-muted text-[13px] mb-4">
                  Henry will be in touch with next steps.
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCertificate}
                  disabled={certBusy}
                >
                  {certBusy ? 'Preparing…' : 'Download certificate'}
                </Button>
                <Button type="button" variant="ghost" onClick={onClose}>
                  Done
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function formatDateTime(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}
